import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SendLiveButton } from "@/lib/notifications";
import {
  X, Loader2, Mail, MessageSquare, Copy, Check, Send, Clock,
  AlertTriangle, ExternalLink, Inbox, Settings as SettingsIcon, Sparkles,
} from "lucide-react";

function CopyBtn({ text, label, testid }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text || "");
          setDone(true);
          toast.success(`${label} copied`);
          setTimeout(() => setDone(false), 1500);
        } catch { toast.error("Couldn't copy"); }
      }}
      className="btn-outline text-xs"
      data-testid={testid}
    >
      {done ? <Check className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

function FollowUpRow({ row, onMarkSent, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState("sms");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiObjection, setAiObjection] = useState(null);  // { type, sms, email_subject, email_body }

  useEffect(() => {
    if (!open || msg) return;
    api.get(`/jobs/${row.job_id}/items/${row.item_id}/follow-up-message`)
      .then(r => setMsg({
        ...r.data,
        // Backend returns a path-only reconsider URL; absolute-ise for the customer
        reconsider_url: r.data.reconsider_path?.startsWith("http")
          ? r.data.reconsider_path
          : `${window.location.origin}${r.data.reconsider_path}`,
        // Substitute the absolute URL in body/SMS
      }))
      .catch(() => toast.error("Couldn't load message"));
  }, [open, msg, row.job_id, row.item_id]);

  const runAiObjection = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/jobs/${row.job_id}/items/${row.item_id}/ai-objection-handler`, {});
      setAiObjection({
        type: r.data.objection_type || "unknown",
        sms: r.data.sms_text || "",
        email_subject: r.data.email_subject || msg?.email_subject || "",
        email_body: r.data.email_body || "",
      });
      toast.success(`AI re-approach ready (${r.data.objection_type || "tailored"})`);
    } catch {
      toast.error("AI Objection Handler failed");
    } finally {
      setAiBusy(false);
    }
  };

  // Resolve final SMS / email body — AI takes priority over template if generated
  const baseSms = msg?.sms_body && msg?.reconsider_url
    ? msg.sms_body.replace(msg.reconsider_path || "", msg.reconsider_url)
    : msg?.sms_body || "";
  const baseEmail = msg?.email_body && msg?.reconsider_url
    ? msg.email_body.replace(msg.reconsider_path || "", msg.reconsider_url)
    : msg?.email_body || "";
  const fullSms = aiObjection?.sms
    ? `${aiObjection.sms}\n\n${msg?.reconsider_url || ""}`.trim()
    : baseSms;
  const fullEmail = aiObjection?.email_body
    ? `${aiObjection.email_body}\n\n${msg?.reconsider_url || ""}`.trim()
    : baseEmail;
  const emailSubject = aiObjection?.email_subject || msg?.email_subject || "";

  const markSent = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${row.job_id}/items/${row.item_id}/mark-followed-up`, {});
      toast.success("Marked as sent");
      onMarkSent?.(row.item_id);
    } catch { toast.error("Failed to mark"); }
    finally { setBusy(false); }
  };

  const dueColour = row.due ? "text-destructive" : "text-muted-foreground";
  const vehicle = [row.vehicle_year, row.vehicle_make, row.vehicle_model].filter(Boolean).join(" ");

  return (
    <div className={`border ${row.due ? "border-destructive/40" : "border-border"} rounded-lg overflow-hidden`} data-testid={`followup-row-${row.item_id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors text-left"
        data-testid={`followup-toggle-${row.item_id}`}
      >
        <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${row.due ? "bg-destructive/15 text-destructive" : "bg-warning/15 text-warning"}`}>
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold truncate">{row.item_name}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${row.due ? "bg-destructive/15 text-destructive" : "bg-warning/15 text-warning"}`}>
              {row.due ? "Due now" : "Pending"}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {row.customer_name || "—"} · {vehicle} · <span className="font-mono-plex">{(row.vehicle_reg || "").toUpperCase()}</span>
          </div>
        </div>
        <div className={`text-xs font-mono-plex ${dueColour} shrink-0`}>
          <Clock className="w-3 h-3 inline -mt-0.5" /> {row.hours_since_decline}h ago
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-border bg-muted/20">
          {!msg ? (
            <div className="py-6 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Preparing message…
            </div>
          ) : (
            <div className="pt-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1 p-1 rounded-lg bg-card border border-border w-fit">
                  <button type="button" onClick={() => setTab("sms")} className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 transition-colors ${tab === "sms" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid={`followup-tab-sms-${row.item_id}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> SMS
                  </button>
                  <button type="button" onClick={() => setTab("email")} className={`px-3 py-1.5 rounded-md text-xs flex items-center gap-2 transition-colors ${tab === "email" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`} data-testid={`followup-tab-email-${row.item_id}`}>
                    <Mail className="w-3.5 h-3.5" /> Email
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {aiObjection && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/30"
                      data-testid={`followup-ai-objection-badge-${row.item_id}`}
                      title="AI-identified objection driving the decline"
                    >
                      <Sparkles className="w-3 h-3" /> {aiObjection.type}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={runAiObjection}
                    disabled={aiBusy}
                    className="btn-outline text-xs"
                    data-testid={`followup-ai-objection-btn-${row.item_id}`}
                    title="Let Claude write a tailored re-approach addressing the customer's likely objection"
                  >
                    {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {aiObjection ? "Regenerate AI re-approach" : "AI Objection Handler"}
                  </button>
                </div>
              </div>

              {tab === "sms" ? (
                <>
                  <div className="rounded-lg bg-card border border-border p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`followup-sms-${row.item_id}`}>
                    {fullSms}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <CopyBtn text={fullSms} label="Copy SMS" testid={`followup-copy-sms-${row.item_id}`} />
                    {row.customer_phone && (
                      <a
                        href={`sms:${encodeURIComponent(row.customer_phone)}?&body=${encodeURIComponent(fullSms)}`}
                        className="btn-outline text-xs"
                        data-testid={`followup-open-sms-${row.item_id}`}
                      >
                        <MessageSquare className="w-3.5 h-3.5" /> Open in Messages
                      </a>
                    )}
                    <SendLiveButton
                      endpoint={`/jobs/${row.job_id}/items/${row.item_id}/send-follow-up`}
                      channel="sms"
                      disabled={!row.customer_phone}
                      onSent={() => onMarkSent?.(row.item_id)}
                      testid={`followup-send-sms-live-${row.item_id}`}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-lg bg-card border border-border overflow-hidden">
                    <div className="px-3 py-2 border-b border-border text-xs font-semibold">{emailSubject}</div>
                    <div className="p-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`followup-email-${row.item_id}`}>{fullEmail}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <CopyBtn text={fullEmail} label="Copy email" testid={`followup-copy-email-${row.item_id}`} />
                    <CopyBtn text={emailSubject} label="Copy subject" testid={`followup-copy-subject-${row.item_id}`} />
                    {row.customer_email && (
                      <a
                        href={`mailto:${encodeURIComponent(row.customer_email)}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(fullEmail)}`}
                        className="btn-outline text-xs"
                        data-testid={`followup-open-email-${row.item_id}`}
                      >
                        <Mail className="w-3.5 h-3.5" /> Open in Mail
                      </a>
                    )}
                    <SendLiveButton
                      endpoint={`/jobs/${row.job_id}/items/${row.item_id}/send-follow-up`}
                      channel="email"
                      disabled={!row.customer_email}
                      onSent={() => onMarkSent?.(row.item_id)}
                      testid={`followup-send-email-live-${row.item_id}`}
                    />
                  </div>
                </>
              )}

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-border">
                <a
                  href={msg.reconsider_url}
                  target="_blank" rel="noreferrer"
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 truncate"
                  data-testid={`followup-link-preview-${row.item_id}`}
                >
                  <ExternalLink className="w-3 h-3" />
                  <span className="truncate font-mono-plex">{msg.reconsider_url}</span>
                </a>
                <button
                  type="button"
                  onClick={markSent}
                  disabled={busy}
                  className="btn-outline text-xs"
                  data-testid={`followup-mark-sent-${row.item_id}`}
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Mark as sent
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FollowUpsModal({ open, onClose, onOpenSettings }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ delay_hours: 48, count: 0, items: [] });
  const [includePending, setIncludePending] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/follow-ups${includePending ? "?include_pending=true" : ""}`);
      setData(r.data);
    } catch { toast.error("Couldn't load follow-ups"); }
    finally { setLoading(false); }
  }, [includePending]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  if (!open) return null;

  const dueRows = data.items.filter(r => r.due);
  const pendingRows = data.items.filter(r => !r.due);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-up" data-testid="followups-modal">
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="label-overline">Re-engage</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Declined work follow-ups</h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Customers who declined a <span className="text-destructive font-semibold">red (urgent)</span> item more than {data.delay_hours}h ago — bring half of them back.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {onOpenSettings && (
              <button onClick={onOpenSettings} className="btn-outline" data-testid="followups-settings-btn" title="Edit follow-up template">
                <SettingsIcon className="w-4 h-4" />
              </button>
            )}
            <button onClick={onClose} className="btn-outline" data-testid="followups-close-btn"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="px-6 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="followups-include-pending">
            <input
              type="checkbox"
              checked={includePending}
              onChange={(e) => setIncludePending(e.target.checked)}
              className="rounded border-border"
            />
            Show pending (declined &lt; {data.delay_hours}h ago)
          </label>
          <span className="text-xs font-mono-plex text-muted-foreground">
            {dueRows.length} due · {pendingRows.length} pending
          </span>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {loading ? (
            <div className="py-12 grid place-items-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : data.items.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" data-testid="followups-empty">
              <Inbox className="w-10 h-10 mx-auto opacity-50" />
              <div className="font-display font-bold text-lg mt-3 text-foreground">No follow-ups right now.</div>
              <p className="text-sm mt-1 max-w-md mx-auto">When a customer declines a red item, they'll appear here {data.delay_hours} hours later.</p>
            </div>
          ) : (
            <>
              {dueRows.length > 0 && (
                <div className="space-y-3">
                  <div className="label-overline text-destructive">Due now ({dueRows.length})</div>
                  {dueRows.map((row) => (
                    <FollowUpRow key={row.item_id} row={row} onMarkSent={refresh} defaultOpen />
                  ))}
                </div>
              )}
              {pendingRows.length > 0 && (
                <div className="space-y-3 pt-4">
                  <div className="label-overline">Pending</div>
                  {pendingRows.map((row) => (
                    <FollowUpRow key={row.item_id} row={row} onMarkSent={refresh} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
